export const VALID_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/svg+xml",
];

export const VALID_FONT_EXTENSIONS = ["ttf", "otf", "woff", "woff2"];

export const VALID_FONT_MIME_TYPES = [
  "font/ttf",
  "font/otf",
  "font/woff",
  "font/woff2",
  "application/x-font-ttf",
  "application/x-font-otf",
  "application/font-woff",
  "application/font-woff2",
  "application/octet-stream",
];

export const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/x-icon": "ico",
  "image/vnd.microsoft.icon": "ico",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/vnd.adobe.photoshop": "psd",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/x-m4v": "m4v",
  "video/x-msvideo": "avi",
  "video/avi": "avi",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/wave": "wav",
  "audio/aac": "aac",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
  "audio/ogg": "ogg",
  "audio/flac": "flac",
  "image/tiff": "tiff",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/postscript": "ps",
  "font/ttf": "ttf",
  "font/otf": "otf",
  "font/woff": "woff",
  "font/woff2": "woff2",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/csv": "csv",
  "text/tab-separated-values": "tsv",
  "application/json": "json",
  "text/html": "html",
  "text/xml": "xml",
  "application/xml": "xml",
  "application/yaml": "yaml",
  "text/yaml": "yaml",
};

export const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  tif: "image/tiff",
  tiff: "image/tiff",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  ico: "image/x-icon",
  cur: "image/x-icon",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  m4v: "video/x-m4v",
  avi: "video/x-msvideo",
  ttf: "font/ttf",
  otf: "font/otf",
  woff: "font/woff",
  woff2: "font/woff2",
  pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  log: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  json: "application/json",
  html: "text/html",
  htm: "text/html",
  xml: "text/xml",
  yaml: "application/yaml",
  yml: "application/yaml",
};

/**
 * The image formats that the Anthropic API accepts, by file extension. Image
 * loaders and the agent payload use this one set, so that no file reaches the
 * API with the wrong media type.
 */
export const IMAGE_EXTENSION_TO_MEDIA_TYPE = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
} as const;

/** An image media type that the Anthropic API accepts. */
export type ImageMediaType =
  (typeof IMAGE_EXTENSION_TO_MEDIA_TYPE)[keyof typeof IMAGE_EXTENSION_TO_MEDIA_TYPE];

/** Returns the media type of a raster image with this extension, or undefined. */
export const lookupImageMediaType = (
  extension: string,
): ImageMediaType | undefined =>
  isImageExtension(extension)
    ? IMAGE_EXTENSION_TO_MEDIA_TYPE[extension]
    : undefined;

function isImageExtension(
  extension: string,
): extension is keyof typeof IMAGE_EXTENSION_TO_MEDIA_TYPE {
  return Object.hasOwn(IMAGE_EXTENSION_TO_MEDIA_TYPE, extension);
}

const asciiAt = (bytes: Uint8Array, start: number, end: number): string =>
  String.fromCharCode(...bytes.subarray(start, end));

/**
 * Returns the media type that the magic bytes of an image show, or null.
 * Catalog exports can have the wrong extension, and Anthropic rejects an image
 * whose declared media type does not agree with its bytes. Takes a
 * `Uint8Array` because the browser bundle also imports this module.
 */
export function sniffImageMediaType(bytes: Uint8Array): ImageMediaType | null {
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    return "image/jpeg";
  }
  if (
    bytes.length >= 12 &&
    asciiAt(bytes, 0, 4) === "RIFF" &&
    asciiAt(bytes, 8, 12) === "WEBP"
  ) {
    return "image/webp";
  }
  if (
    bytes.length >= 6 &&
    ["GIF87a", "GIF89a"].includes(asciiAt(bytes, 0, 6))
  ) {
    return "image/gif";
  }
  return null;
}

export const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
] as const;

/**
 * Both tables above are keyed on values we don't choose — MIME types off the
 * wire and extensions parsed out of user-supplied filenames — so a bare `[]`
 * read returns an inherited `Object.prototype` member for a handful of names
 * (`toString`, `constructor`, …). That value is truthy and is not a string, so
 * it flows on as if it were a real mapping and then throws somewhere further
 * downstream. Look up through these instead of indexing directly.
 */
export const lookupMimeTypeForExtension = (
  extension: string,
): string | undefined =>
  Object.hasOwn(EXTENSION_TO_MIME_TYPE, extension)
    ? EXTENSION_TO_MIME_TYPE[extension]
    : undefined;

export const lookupExtensionForMimeType = (
  mimeType: string,
): string | undefined =>
  Object.hasOwn(MIME_TYPE_TO_EXTENSION, mimeType)
    ? MIME_TYPE_TO_EXTENSION[mimeType]
    : undefined;

/** Non-standard spellings that sources send, keyed to the registered type. */
const MIME_TYPE_ALIASES: Readonly<Record<string, string>> = {
  "image/jpg": "image/jpeg",
};

/**
 * The registered spelling of a MIME type: trimmed, lowercased, and with a
 * known alias such as `image/jpg` mapped to its standard name.
 */
export function canonicalizeMimeType(mimeType: string): string {
  const normalized = mimeType.trim().toLowerCase();
  return Object.hasOwn(MIME_TYPE_ALIASES, normalized)
    ? (MIME_TYPE_ALIASES[normalized] ?? normalized)
    : normalized;
}

/**
 * Prefix check rather than a lookup against {@link VALID_IMAGE_MIME_TYPES}:
 * browsers report image types that list doesn't carry (`image/gif`,
 * `image/avif`, `image/bmp`), and callers that gate image-only work need all of
 * them, not just the ones we accept as uploads.
 */
export const isImageMimeType = (mimeType: string): boolean =>
  mimeType.startsWith("image/");

const GENERIC_MIME_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
]);

/**
 * The declared MIME type without parameters, or the one the extension implies
 * when the browser sent none or a generic one.
 */
export function resolveFileMimeType(
  filetype: string,
  filename: string,
): string | undefined {
  const declared = filetype.split(";")[0]?.trim().toLowerCase() ?? "";
  if (!GENERIC_MIME_TYPES.has(declared)) {
    return declared;
  }
  return lookupMimeTypeForExtension(getFileExtension(filename).slice(1));
}

/**
 * Lowercased extension including the dot (".mp4"), or "" when the name has
 * none. A trailing dot ("archive.") counts as none.
 *
 * Note the two conventions in this file: the lookup tables above are keyed
 * without the dot, this returns it with one.
 */
export const getFileExtension = (fileName: string): string => {
  const index = fileName.lastIndexOf(".");
  if (index < 0 || index === fileName.length - 1) {
    return "";
  }
  return fileName.slice(index).toLowerCase();
};

/**
 * The only extensions we refuse. Attachments are otherwise unrestricted: the
 * backend stores the bytes in S3 and writes them verbatim into the agent's
 * sandboxed filesystem, and the type-specific handling (PDF extraction, image
 * content nodes, font/logo validation) is gated server-side where it's needed.
 * So we block only what is obviously useless or hostile — compiled binaries,
 * installers, disk images — where accepting the file could only waste payload
 * budget or signal a mis-drop.
 *
 * Shared rather than mirrored: the browser check is advisory (a presigned
 * upload can be initiated for any filename), so the server has to enforce the
 * same set, and two copies would drift.
 */
export const BLOCKED_FILE_EXTENSIONS = [
  // Windows executables and launchers
  ".exe",
  ".msi",
  ".msix",
  ".bat",
  ".cmd",
  ".com",
  ".scr",
  ".lnk",
  // Compiled libraries
  ".dll",
  ".so",
  ".dylib",
  // macOS installers and bundles
  ".dmg",
  ".pkg",
  ".app",
  // Mobile and Linux packages
  ".apk",
  ".ipa",
  ".deb",
  ".rpm",
  // Disk images and raw binaries
  ".iso",
  ".bin",
  ".jar",
] as const;

const BLOCKED_EXTENSION_SET = new Set<string>(BLOCKED_FILE_EXTENSIONS);

export const isBlockedFileExtension = (fileName: string): boolean =>
  BLOCKED_EXTENSION_SET.has(getFileExtension(fileName));

export function inferImageMimeType(file: File): string {
  if (file.type && isImageMimeType(file.type)) {
    return file.type;
  }
  // Lookup tables here are keyed without the leading dot.
  const extension = getFileExtension(file.name).slice(1);
  const contentType = extension
    ? lookupMimeTypeForExtension(extension)
    : undefined;
  if (!contentType || !isImageMimeType(contentType)) {
    throw new Error("Unsupported image type");
  }
  return contentType;
}
